import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CommandBarProvider } from "@/components/command/CommandBarProvider";
import { ThemeProvider } from "@/context/ThemeContext";
import { LocaleProvider } from "@/context/LocaleContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { ToastContainer } from "@/components/ui/Toast";
import { RouteErrorBoundary } from "@/components/shared/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RoleGate } from "@/components/auth/RoleGate";
import { AppShell } from "@/components/layout/AppShell";
import { useTranslation } from "react-i18next";

// Pages
import { LoginPage } from "@/pages/auth/LoginPage";
import { SignupPage } from "@/pages/auth/SignupPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { OnboardingWizardPage } from "@/pages/onboarding/OnboardingWizardPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { SetupPasswordPage } from "@/pages/auth/SetupPasswordPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { SSOCallbackPage } from "@/pages/auth/SSOCallbackPage";
import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import { EmployeeListPage } from "@/pages/employees/EmployeeListPage";
import { EmployeeProfilePage } from "@/pages/employees/EmployeeProfilePage";
import { EmployeeCreatePage } from "@/pages/employees/EmployeeCreatePage";
import { CsvImportPage } from "@/pages/employees/CsvImportPage";
import { OrgChartPage } from "@/pages/employees/OrgChartPage";
import { DirectoryPage } from "@/pages/directory/DirectoryPage";
import { AnnouncementsPage } from "@/pages/announcements/AnnouncementsPage";
import { MyProfilePage } from "@/pages/my/MyProfilePage";
import { EmailPreferencesPage } from "@/pages/my/EmailPreferencesPage";
import { SettingsPage } from "@/pages/settings/SettingsPage";
import { SecuritySettings } from "@/pages/settings/SecuritySettings";
import { TimeOffPage } from "@/pages/leave/TimeOffPage";
import { LeaveCalendarPage } from "@/pages/leave/LeaveCalendarPage";
import { ApprovalsPage } from "@/pages/approvals/ApprovalsPage";
import { DocumentsPage } from "@/pages/documents/DocumentsPage";
import { MyDocumentsPage } from "@/pages/documents/MyDocumentsPage";
import { MyDocumentDetailPage } from "@/pages/documents/MyDocumentDetailPage";
import { EmployeeFormSubmissionPage } from "@/pages/employees/EmployeeFormSubmissionPage";
import { AccountSecurityPage } from "@/pages/auth/AccountSecurityPage";
import { Setup2FAPage } from "@/pages/auth/Setup2FAPage";
import { TimeTrackingPage } from "@/pages/time/TimeTrackingPage";
import { InvoicesPage } from "@/pages/invoices/InvoicesPage";
import { InvoiceDetailPage } from "@/pages/invoices/InvoiceDetailPage";
import { InvoiceClientsPage } from "@/pages/invoices/InvoiceClientsPage";
import { RecurringInvoicesPage } from "@/pages/invoices/RecurringInvoicesPage";
import { ComplaintsPage } from "@/pages/complaints/ComplaintsPage";
import { ComplaintDetailPage } from "@/pages/complaints/ComplaintDetailPage";
import { AnonymousComplaintStatusPage } from "@/pages/complaints/AnonymousComplaintStatusPage";
import { ReportsPage } from "@/pages/reports/ReportsPage";
import { ReportBuilderPage } from "@/pages/reports/ReportBuilderPage";
import { SavedReportsPage } from "@/pages/reports/SavedReportsPage";
import { AuditLogPage } from "@/pages/audit/AuditLogPage";
import { AIAssistantPage } from "@/pages/ai/AIAssistantPage";
import { AgentsPage } from "@/pages/agents/AgentsPage";
import { AgentDetailPage } from "@/pages/agents/AgentDetailPage";
import { PulseSurveysPage } from "@/pages/pulse/PulseSurveysPage";
import { PulseBuilderPage } from "@/pages/pulse/PulseBuilderPage";
import { PulseRespondPage } from "@/pages/pulse/PulseRespondPage";
import { PulseResultsPage } from "@/pages/pulse/PulseResultsPage";
import { PulseDashboardPage } from "@/pages/pulse/PulseDashboardPage";
import { CobraPage } from "@/pages/cobra/CobraPage";
import { CarrierInvoicesPage } from "@/pages/benefits/CarrierInvoicesPage";
import { BenefitsPlansPage } from "@/pages/benefits/BenefitsPlansPage";
import { EnrollmentPeriodsPage } from "@/pages/benefits/EnrollmentPeriodsPage";
import { BenefitsDashboardPage } from "@/pages/benefits/BenefitsDashboardPage";
import { BenefitsEnrollmentPage } from "@/pages/benefits/BenefitsEnrollmentPage";
import { BenefitsWizardPage } from "@/pages/benefits/BenefitsWizardPage";
import { BenefitsLifeEventPage } from "@/pages/benefits/BenefitsLifeEventPage";
import { LifeEventsPage as BenefitsLifeEventsPage } from "@/pages/benefits/LifeEventsPage";
import { PayrollDeductionsPage } from "@/pages/benefits/PayrollDeductionsPage";
import { CompliancePage } from "@/pages/compliance/CompliancePage";
import { TrainingCoursesPage } from "@/pages/training/TrainingCoursesPage";
import { MyTrainingPage } from "@/pages/training/MyTrainingPage";
import { TrainingDashboardPage } from "@/pages/training/TrainingDashboardPage";
import { TalentMapPage } from "@/pages/talent/TalentMapPage";
import { EmployeeSkillsPage } from "@/pages/talent/EmployeeSkillsPage";
import { SuccessionPage } from "@/pages/succession/SuccessionPage";
import { PositionDetailPage } from "@/pages/succession/PositionDetailPage";
import { RecruitingPage } from "@/pages/recruiting/RecruitingPage";
import { PerformancePage } from "@/pages/performance/PerformancePage";
import { AssetsPage } from "@/pages/assets/AssetsPage";
import { OffboardingListPage } from "@/pages/offboarding/OffboardingListPage";
import { OffboardingDetailPage } from "@/pages/offboarding/OffboardingDetailPage";
import { MyPrivacyPage } from "@/pages/privacy/MyPrivacyPage";
import { TeamDashboardPage } from "@/pages/team/TeamDashboardPage";
import { TeamOverdueDocsPage } from "@/pages/team/TeamOverdueDocsPage";
import { ClaimsSharedPage } from "@/pages/benefits/ClaimsSharedPage";
import { OneOnOnesPage } from "@/pages/one-on-ones/OneOnOnesPage";
import { CompensationPage } from "@/pages/compensation/CompensationPage";
import { CompensationBenchmarkPage } from "@/pages/compensation/CompensationBenchmarkPage";
import { ExpensesPage } from "@/pages/expenses/ExpensesPage";
import { WorkforcePage } from "@/pages/workforce/WorkforcePage";
import { PredictPage } from "@/pages/predict/PredictPage";
import { SupportConsolePage } from "@/pages/admin/SupportConsolePage";
import { OpsAlertsPage } from "@/pages/admin/OpsAlertsPage";
import { RequirePlatformAdmin } from "@/components/auth/RequirePlatformAdmin";
import { PrivacyPage } from "@/pages/legal/PrivacyPage";
import { TermsPage } from "@/pages/legal/TermsPage";
import { TrustPage } from "@/pages/legal/TrustPage";
import { DemoPage } from "@/pages/demo/DemoPage";
import {
  PublicCareersDetailPage,
  PublicCareersListPage,
} from "@/pages/public/CareersPage";
import { DemoBlockedBridge } from "@/components/shared/DemoBlockedBridge";

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LocaleProvider>
        <NotificationProvider>
          <AuthProvider>
            {/* Cmd+K command bar overlay — single mount, accessible
                from every authed page. Owns the keyboard shortcut +
                modal state; the dashboard renders its own inline
                instance separately. Sits inside AuthProvider because
                the bar makes authenticated API calls. */}
            <CommandBarProvider>
            {/* Outer boundary catches crashes in the auth-bootstrap
                layer and public routes. AppShell mounts a second
                RouteErrorBoundary inside itself so an authed-route
                crash stays contained to the main content area without
                destroying the sidebar + top bar. */}
            <RouteErrorBoundary scope="app-root">
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/setup-password" element={<SetupPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/sso/callback" element={<SSOCallbackPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                {/* Public Trust & Security page — lives at /security so
                    prospects can find it at the obvious URL. The per-user
                    MFA/security settings screen moved to /account/security
                    to make room. Kept /trust as an alias for any old
                    marketing links. */}
                <Route path="/security" element={<TrustPage />} />
                <Route path="/trust" element={<TrustPage />} />
                <Route path="/security-overview" element={<TrustPage />} />
                {/* Public interactive demo — no auth, no AppShell. */}
                <Route path="/demo" element={<DemoPage />} />
                {/* Public read-only broker claims share (signed token,
                    no auth, no AppShell). */}
                <Route
                  path="/claims/shared/:token"
                  element={<ClaimsSharedPage />}
                />

                {/*
                  Public careers page — ``/{org_slug}/careers`` and
                  ``/{org_slug}/careers/{job_id}``. No auth, no
                  sidebar; renders the bare branded surface for
                  external candidates. Tenant scoping is by URL
                  slug, validated server-side. The slug pattern is
                  loose enough to share routes with the SPA's
                  authed shell but specific enough to not collide
                  (the ``/careers`` segment makes the match).
                */}
                <Route
                  path="/:slug/careers"
                  element={<PublicCareersListPage />}
                />
                <Route
                  path="/:slug/careers/:jobId"
                  element={<PublicCareersDetailPage />}
                />
                <Route
                  path="/anon-status/:orgSlug/:token"
                  element={<AnonymousComplaintStatusPage />}
                />

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  {/* Onboarding wizard renders outside the main AppShell
                      so there's no sidebar/nav distraction for the first
                      admin landing straight from /signup. */}
                  <Route path="/onboarding" element={<OnboardingWizardPage />} />
                  <Route element={<AppShell />}>
                    <Route path="/" element={<DashboardPage />} />

                    {/* People */}
                    {/* /employees is a leadership surface — manager+ only.
                        Backend filters list payload to a directory-safe
                        shape for non-admins, but the page chrome shows
                        admin actions (New, Import, manage roles) and
                        shouldn't render for plain employees. They use
                        /directory + /my/profile instead. */}
                    <Route
                      path="/employees"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}
                          fallback={<NotAuthorized />}
                        >
                          <EmployeeListPage />
                        </RoleGate>
                      }
                    />
                    <Route path="/employees/org-chart" element={<OrgChartPage />} />
                    <Route path="/directory" element={<DirectoryPage />} />
                    <Route path="/announcements" element={<AnnouncementsPage />} />
                    <Route path="/my/profile" element={<MyProfilePage />} />
                    <Route
                      path="/my/email-preferences"
                      element={<EmailPreferencesPage />}
                    />
                    <Route path="/one-on-ones" element={<OneOnOnesPage />} />
                    <Route
                      path="/compensation"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <CompensationPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/compensation/benchmarks"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <CompensationBenchmarkPage />
                        </RoleGate>
                      }
                    />
                    <Route path="/expenses" element={<ExpensesPage />} />
                    <Route
                      path="/workforce"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <WorkforcePage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/predict"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <PredictPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/my/team"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}
                          fallback={<NotAuthorized />}
                        >
                          <TeamDashboardPage />
                        </RoleGate>
                      }
                    />
                    {/* Deep-link destination for the "documents overdue
                        on your team" alert on /my/team. Same role gate
                        as the parent — non-managers can't see anything
                        useful here anyway (the backend returns []), but
                        the route gate keeps consistency with /my/team
                        and avoids surfacing the page in nav for roles
                        that can't act on it. */}
                    <Route
                      path="/team/overdue-documents"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}
                          fallback={<NotAuthorized />}
                        >
                          <TeamOverdueDocsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/employees/import"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <CsvImportPage />
                        </RoleGate>
                      }
                    />
                    {/* ``/employees/new`` must be registered BEFORE the
                        ``:id`` route so react-router doesn't treat "new"
                        as an employee id and send the profile page on a
                        wild goose chase looking up a nonexistent
                        employee (the original "Failed to load employee"
                        bug). */}
                    <Route
                      path="/employees/new"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <EmployeeCreatePage />
                        </RoleGate>
                      }
                    />
                    <Route path="/employees/:id" element={<EmployeeProfilePage />} />

                    {/* Leave */}
                    <Route path="/leave" element={<TimeOffPage />} />
                    <Route path="/leave/calendar" element={<LeaveCalendarPage />} />

                    {/* Time */}
                    <Route path="/time" element={<TimeTrackingPage />} />

                    <Route
                      path="/invoices"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <InvoicesPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/invoices/recurring"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <RecurringInvoicesPage />
                        </RoleGate>
                      }
                    />
                    {/* Clients (recipient address book). Must be
                        registered BEFORE /invoices/:id so the static
                        "clients" segment isn't captured as an
                        invoice UUID. */}
                    <Route
                      path="/invoices/clients"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <InvoiceClientsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/invoices/clients/:id"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <InvoiceClientsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/invoices/:id"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN"]}>
                          <InvoiceDetailPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/approvals"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}>
                          <ApprovalsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/recruiting"
                      element={
                        <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}>
                          <RecruitingPage />
                        </RoleGate>
                      }
                    />
                    <Route path="/performance" element={<PerformancePage />} />
                    <Route path="/assets" element={<AssetsPage />} />
                    <Route
                      path="/offboarding"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <OffboardingListPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/offboarding/:id"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <OffboardingDetailPage />
                        </RoleGate>
                      }
                    />
                    {/* Complaints — HR_ADMIN+ only. The backend already
                        filters the list to ``submitted_by=user`` for
                        employees, but the page chrome (queue stats,
                        overdue panel, severity filters) is admin-only
                        and shouldn't render for them. Employees submit
                        a new complaint via /complaints/new (open).
                        Their own submissions surface in their inbox. */}
                    <Route
                      path="/complaints"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <ComplaintsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/complaints/:id"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <ComplaintDetailPage />
                        </RoleGate>
                      }
                    />
                    {/* Documents library — HR_ADMIN+MANAGER (org-wide
                        templates + audit log). Employees see their own
                        documents at /my-documents, which is gated to
                        EMPLOYEE+MANAGER below. Backend list endpoint
                        also auto-scopes to the caller's employee_id
                        for plain employees. */}
                    <Route
                      path="/documents"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}
                          fallback={<NotAuthorized />}
                        >
                          <DocumentsPage />
                        </RoleGate>
                      }
                    />
                    {/* COBRA admin — gated to MANAGE_EMPLOYEES via the
                        backend; the SPA route is authed-only and the
                        page itself simply 403s if the API refuses. */}
                    <Route
                      path="/cobra"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <CobraPage />
                        </RoleGate>
                      }
                    />
                    {/* Carrier invoice reconciliation. Detail view is
                        the same component, switched by ?invoice=<id>. */}
                    <Route
                      path="/benefits/invoices"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <CarrierInvoicesPage />
                        </RoleGate>
                      }
                    />
                    {/* Compliance Agent — Sentinel's daily findings,
                        operational view. Distinct from
                        /settings/compliance which is the configure-
                        requirements surface. */}
                    <Route
                      path="/compliance"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <CompliancePage />
                        </RoleGate>
                      }
                    />
                    {/* Benefits module — Lenavio's Employee Navigator
                        replacement. Admin: plans, periods, dashboard,
                        life-event review, payroll export. Employee:
                        enrollment portal + life-event reporting. */}
                    <Route
                      path="/benefits/dashboard"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <BenefitsDashboardPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/benefits/plans"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <BenefitsPlansPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/benefits/periods"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <EnrollmentPeriodsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/benefits/life-events"
                      element={<BenefitsLifeEventsPage />}
                    />
                    <Route
                      path="/benefits/enrollment"
                      element={<BenefitsEnrollmentPage />}
                    />
                    {/* CBIZ-demo wizard — the employee-facing
                        decision flow (recommendations, comparison,
                        confirmation). Lives at /benefits/enroll so
                        the existing /benefits/enrollment legacy
                        flow keeps working until we sunset it. */}
                    <Route
                      path="/benefits/enroll"
                      element={<BenefitsWizardPage />}
                    />
                    <Route
                      path="/benefits/life-event"
                      element={<BenefitsLifeEventPage />}
                    />
                    <Route
                      path="/benefits/payroll"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <PayrollDeductionsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/my-documents"
                      element={<MyDocumentsPage />}
                    />
                    <Route
                      path="/my/documents/:id"
                      element={<MyDocumentDetailPage />}
                    />
                    {/* Admin form-submission review — gated to roles
                        that hold MANAGE_DOCUMENTS via the page itself
                        (the in-page action buttons hide for everyone
                        else; the API also enforces the permission). */}
                    <Route
                      path="/employees/:employeeId/forms/:assignmentId"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}
                          fallback={<NotAuthorized />}
                        >
                          <EmployeeFormSubmissionPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/account/security"
                      element={<AccountSecurityPage />}
                    />
                    <Route
                      path="/account/security/setup-2fa"
                      element={<Setup2FAPage />}
                    />
                    <Route
                      path="/reports"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <ReportsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/reports/builder"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <ReportBuilderPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/audit-log"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <AuditLogPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/reports/saved"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <SavedReportsPage />
                        </RoleGate>
                      }
                    />
                    <Route path="/ai" element={<AIAssistantPage />} />
                    <Route
                      path="/agents"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <AgentsPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/agents/:name"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <AgentDetailPage />
                        </RoleGate>
                      }
                    />

                    {/* Lenavio Pulse — engagement surveys + eNPS.
                        ``/pulse`` and ``/pulse/:id/respond`` open to
                        every authed role; the rest are HR/admin
                        only. The backend plan-gates everything via
                        require_feature("pulse_surveys"), so a Free
                        org sees a 402 on any of these routes. */}
                    <Route path="/pulse" element={<PulseSurveysPage />} />
                    <Route
                      path="/pulse/dashboard"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <PulseDashboardPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/pulse/new"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <PulseBuilderPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/pulse/:id/edit"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <PulseBuilderPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/pulse/:id/respond"
                      element={<PulseRespondPage />}
                    />
                    <Route
                      path="/pulse/:id/results"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <PulseResultsPage />
                        </RoleGate>
                      }
                    />

                    {/* Lenavio Learn — training/LMS. ``/training``
                        is the HR-only library; ``/my-training`` is
                        the employee surface; ``/training/dashboard``
                        is the org-wide rollup + compliance health.
                        Backend gates everything behind
                        require_feature("training"). */}
                    <Route
                      path="/training"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <TrainingCoursesPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/training/dashboard"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <TrainingDashboardPage />
                        </RoleGate>
                      }
                    />
                    <Route path="/my-training" element={<MyTrainingPage />} />

                    {/* Lenavio Talent Map — HR-only. Backend
                        plan-gates everything via require_feature
                        ("talent_map"); RoleGate adds the role
                        check at the route boundary. */}
                    <Route
                      path="/talent"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <TalentMapPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/talent/employees/:id"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}
                          fallback={<NotAuthorized />}
                        >
                          <EmployeeSkillsPage />
                        </RoleGate>
                      }
                    />

                    {/* Lenavio Succession — HR-only. Backend
                        plan-gates everything via require_feature
                        ("succession"). Position detail surface
                        carries the candidate-management UX. */}
                    <Route
                      path="/succession"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <SuccessionPage />
                        </RoleGate>
                      }
                    />
                    <Route
                      path="/succession/positions/:id"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN", "HR_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <PositionDetailPage />
                        </RoleGate>
                      }
                    />

                    {/* Per-user security settings — all roles */}
                    <Route path="/account/security" element={<SecuritySettings />} />
                    {/* Legacy alias — the MFA screen used to live at
                        /security before the public Trust page reclaimed
                        that URL. Kept so existing bookmarks still land
                        on the right page. */}
                    <Route path="/security-legacy" element={<SecuritySettings />} />

                    {/* Self-service privacy (LGPD/GDPR/CCPA) — every role */}
                    <Route path="/my-privacy" element={<MyPrivacyPage />} />

                    {/* Platform support console + DevOps watchdog — wrapped in
                        RequirePlatformAdmin so non-platform-admins get redirected
                        to "/" rather than seeing the page render and 403 on data fetch. */}
                    <Route
                      path="/admin"
                      element={
                        <RequirePlatformAdmin>
                          <SupportConsolePage />
                        </RequirePlatformAdmin>
                      }
                    />
                    <Route
                      path="/admin/ops"
                      element={
                        <RequirePlatformAdmin>
                          <OpsAlertsPage />
                        </RequirePlatformAdmin>
                      }
                    />

                    {/* Org-level settings — SUPER_ADMIN only */}
                    <Route
                      path="/settings/*"
                      element={
                        <RoleGate
                          allowed={["SUPER_ADMIN"]}
                          fallback={<NotAuthorized />}
                        >
                          <SettingsPage />
                        </RoleGate>
                      }
                    />
                  </Route>
                </Route>
              </Routes>
              <DemoBlockedBridge />
              <ToastContainer />
            </RouteErrorBoundary>
            </CommandBarProvider>
          </AuthProvider>
        </NotificationProvider>
        </LocaleProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function NotAuthorized() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-24">
      <h2 className="text-2xl font-bold font-heading text-text-primary">
        {t("common.accessDenied")}
      </h2>
      <p className="text-text-muted mt-2">{t("common.accessDeniedBody")}</p>
    </div>
  );
}
